import { useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

const SIZES = {
  sm: { height: 'var(--control-sm)', padding: '0 var(--space-4)', font: 'var(--text-sm)', gap: 'var(--space-2)', icon: 16 },
  md: { height: 'var(--control-md)', padding: '0 var(--space-5)', font: 'var(--text-md)', gap: 'var(--space-2)', icon: 18 },
  lg: { height: 'var(--control-lg)', padding: '0 var(--space-6)', font: 'var(--text-lg)', gap: 'var(--space-3)', icon: 20 },
} as const;

type Variant = {
  base: CSSProperties;
  hover: CSSProperties;
};

const VARIANTS = {
  primary: {
    base: {
      background: 'var(--brand)',
      color: 'var(--brand-on)',
      border: '1px solid transparent',
      boxShadow: 'var(--shadow-sm)',
    },
    hover: { background: 'var(--brand-hover)' },
  },
  secondary: {
    base: { background: 'var(--accent-subtle)', color: 'var(--accent-hover)', border: '1px solid transparent' },
    hover: { background: 'var(--accent-subtle)', filter: 'brightness(0.97)' },
  },
  outline: {
    base: { background: 'transparent', color: 'var(--text)', border: '1px solid var(--border-strong)' },
    hover: { background: 'var(--surface-sunken)' },
  },
  ghost: {
    base: { background: 'transparent', color: 'var(--text-muted)', border: '1px solid transparent' },
    hover: { background: 'var(--surface-sunken)' },
  },
  danger: {
    base: {
      background: 'var(--danger)',
      color: 'var(--danger-on)',
      border: '1px solid transparent',
      boxShadow: 'var(--shadow-sm)',
    },
    hover: { filter: 'brightness(0.95)' },
  },
} as const satisfies Record<string, Variant>;

export type ButtonProps = {
  children?: ReactNode;
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  iconStart?: IconName | string;
  iconEnd?: IconName | string;
  fullWidth?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/** Button — primary action control. Press gives a soft squish, never a colour flash. */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  iconStart,
  iconEnd,
  fullWidth = false,
  disabled = false,
  type = 'button',
  style,
  ...rest
}: ButtonProps) {
  const s = SIZES[size] ?? SIZES.md;
  const v = VARIANTS[variant] ?? VARIANTS.primary;
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const styles: CSSProperties = {
    display: fullWidth ? 'flex' : 'inline-flex',
    width: fullWidth ? '100%' : undefined,
    alignItems: 'center',
    justifyContent: 'center',
    gap: s.gap,
    height: s.height,
    padding: s.padding,
    fontFamily: 'var(--font-body)',
    fontSize: s.font,
    fontWeight: 'var(--fw-bold)' as unknown as number,
    lineHeight: 1,
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition:
      'background var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-soft), filter var(--duration-fast) var(--ease-standard)',
    transform: active && !disabled ? 'scale(0.97)' : 'scale(1)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    ...v.base,
    ...(hover && !disabled ? v.hover : null),
    ...style,
  };

  return (
    <button
      type={type}
      disabled={disabled}
      style={styles}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      {...rest}
    >
      {iconStart ? <Icon name={iconStart} size={s.icon} /> : null}
      {children}
      {iconEnd ? <Icon name={iconEnd} size={s.icon} /> : null}
    </button>
  );
}
